import { Injectable } from '@angular/core';
import {
  AccountPreferenceCategory,
  AccountPreferenceType,
  IAccountPreferences,
  MsGuestsService,
} from '@upr-web-primer/ms-guests';

/**
 * Typed stub for the `mergeAccountPreferences` method that exists at runtime
 * on `AccountPreferencesMsGuestsClass` but is not yet declared in the
 * `@upr-web-primer/ms-guests` typings.
 *
 * TODO(TECH-DEBT): Remove once ms-guests ships typings for mergeAccountPreferences.
 */
export interface AccountPreferencesWithMerge {
  mergeAccountPreferences(
    guestId: string,
    instanceId: string,
  ): Promise<IAccountPreferences[]>;
}

/**
 * Service responsible for merging unauthenticated (anonymous) account preferences
 * into the authenticated guest profile after login or account creation.
 *
 * When a guest favorites places while unauthenticated, those preferences are stored
 * against an instance_id. After the guest logs in or creates an account, this service
 * checks for any unauthenticated favorite places and merges them into the
 * authenticated profile using the ms-guests accountPreferences API.
 */
@Injectable({
  providedIn: 'root',
})
export class PreferenceMergeService {
  /** Used as a promise-based guard to prevent duplicate API. */
  private mergePromise: Promise<IAccountPreferences[] | null> | null = null;

  constructor(private readonly msGuests: MsGuestsService) {}

  /**
   * Checks for unauthenticated favorite place preferences and merges them
   * into the authenticated guest profile.
   *
   * Treated as a fire-and-forget to not interupt guest login flow.
   *
   * @param {string} [guestId] - The authenticated guest ID. Falls back to helper.getGuestId().
   * @param {string} [instanceId] - The unauthenticated instance ID. Falls back to helper.getInstanceId().
   * @returns {Promise<IAccountPreferences[] | null>} The merged preferences, or null if no merge was needed or an error occurred.
   */
  mergeUnauthenticatedFavorites(
    guestId?: string,
    instanceId?: string,
  ): Promise<IAccountPreferences[] | null> {
    if (this.mergePromise) {
      return this.mergePromise;
    }

    this.mergePromise = this.executeMerge(guestId, instanceId);
    return this.mergePromise;
  }

  private async executeMerge(
    guestId?: string,
    instanceId?: string,
  ): Promise<IAccountPreferences[] | null> {
    try {
      const resolvedInstanceId =
        instanceId || this.msGuests.helper.getInstanceId();
      const resolvedGuestId = guestId || this.msGuests.helper.getGuestId();

      if (!resolvedInstanceId || !resolvedGuestId) {
        this.mergePromise = null;
        return null;
      }

      const unauthPreferences =
        await this.msGuests.accountPreferences.getUnauthenticatedAccountPreferences(
          resolvedInstanceId,
          undefined,
          {
            preference_category: AccountPreferenceCategory.FAVORITE,
            preference_type: AccountPreferenceType.PLACE,
          },
        );

      if (!unauthPreferences?.length) {
        return null;
      }

      // TODO(TECH-DEBT): Remove cast once ms-guests ships typings for mergeAccountPreferences.
      return await (
        this.msGuests
          .accountPreferences as unknown as AccountPreferencesWithMerge
      ).mergeAccountPreferences(resolvedGuestId, resolvedInstanceId);
    } catch (_error) {
      // Preference merge errors are handled silently to avoid disrupting the login flow
      this.mergePromise = null;
      return null;
    }
  }
}
